import dayjs from 'dayjs';
import type { OrderSummaryLineItem } from '../types/OrderHistory.types';

interface LineItemGroupContainer {
  groupId: string;
  lineItems: OrderSummaryLineItem[];
}

/**
 * Returns true if the argument is a LineItemGroupContainer, false otherwise
 */
export const isObjectLineItemGroupContainer = (
  arg: LineItemGroupContainer | OrderSummaryLineItem
): arg is LineItemGroupContainer => {
  return 'lineItems' in arg;
};

/**
 * Groups line items into into a container if part of an item group, otherwise returns the line item as-is.
 * If a line item is a standalone item (i.e. mug), it will be returned as-is.
 * If a line item is part of a group (i.e. t-shirt), it will share a container with the items in its group.
 * This container will contain an array of the grouped items along with their groupId: { groupId: 'a1b2c3', lineItems: [ groupItemA, groupItemB ] }
 * @param { LineItemDetails[] } lineItems - a list of line item objects as returned on an order by BOP
 */
const generateItemContainers = (lineItems: OrderSummaryLineItem[]) => {
  const lineItemContainers: (LineItemGroupContainer | OrderSummaryLineItem)[] =
    [];

  if (!lineItems) return lineItemContainers;

  lineItems.forEach((lineItem) => {
    if (!lineItem) return;

    if (lineItem.groupInfo?.groupId) {
      // group line item
      const groupIndex = lineItemContainers.findIndex(
        (grouping) =>
          isObjectLineItemGroupContainer(grouping) &&
          grouping.groupId === lineItem.groupInfo?.groupId
      );
      if (groupIndex !== -1) {
        // @ts-expect-error - group already exists, push another line item to it
        lineItemContainers[groupIndex]?.lineItems.push(lineItem);
      } else {
        // group doesn't exist, create it
        lineItemContainers.push({
          groupId: lineItem.groupInfo.groupId,
          lineItems: [lineItem],
        });
      }
    } else {
      // standalone line item
      lineItemContainers.push(lineItem);
    }
  });

  return lineItemContainers;
};

/**
 * Returns a Day.js object so it can be properly compared by sort()
 * Returns undefined if the given date is empty/undefined or in a format incompatible with Day.js
 * @param {(string|number)} date - a date string returned on an order ('2021-08-05') or a date number in milliseconds (1628136000000)
 */
const convertDate = (date: string | number | undefined) => {
  if (date === null || date === '' || date === undefined) {
    return undefined;
  }

  // Convert given date to Day.js object
  const dayjsDate = dayjs(date);

  // Return undefined if the date is invalid, which happens when Day.js is given an incompatible value (i.e. "doggo", "thefirstofaugust")
  if (!dayjsDate.isValid()) {
    return undefined;
  }

  return dayjsDate;
};

/**
 * Returns the promised arrival date of the arg
 * @param { LineItemGroupContainer | LineItemDetails } arg - an item group container or a line item
 */
const getPromisedArrivalDate = (
  arg: LineItemGroupContainer | OrderSummaryLineItem
) => {
  if (!arg) return undefined;

  // If the arg is an item container with an item group, return the latest promised arrival date of all the line items in the item group
  if (isObjectLineItemGroupContainer(arg) && arg.groupId) {
    // Math.max() will return the date in milliseconds, so we wrap it with convertDate() to get a Date object
    const latestArrivalDate = convertDate(
      Math.max(
        ...arg.lineItems.map(
          (e) => convertDate(e.promisedArrivalDate)?.valueOf() || 0
        )
      )
    );
    return latestArrivalDate;
  }

  // If the arg is an item container with a line item, return its promised arrival date
  if (isObjectLineItemGroupContainer(arg) && arg.lineItems.length > 0) {
    return convertDate(arg.lineItems[0]!.promisedArrivalDate);
  }

  // If the arg is a line item, return its promised arrival date
  if (!isObjectLineItemGroupContainer(arg) && arg.promisedArrivalDate) {
    return convertDate(arg.promisedArrivalDate);
  }
  // If we can't find the promised arrival date, return undefined
  return undefined;
};

/**
 * Returns true if the given date occurs in the past
 * @param {Object} date - a Day.js object
 */
const isDateInPast = (date: dayjs.Dayjs) => {
  const today = dayjs();
  if (date.isBefore(today, 'day')) {
    return true;
  }

  return false;
};

/**
 * Sorts args by earliest to latest promised arrival date, placing args with unknown arrival dates at the end of the list
 * @param { (LineItemGroupContainer | LineItemDetails)[] } args - a list of item group containers or line items
 * @param { boolean } filterOutPastArrivalDates - if true, places line items with arrival dates in the past at the end of the list
 */
const sortByArrivalDate = (
  args: (LineItemGroupContainer | OrderSummaryLineItem)[],
  filterOutPastArrivalDates?: boolean
) => {
  if (!args || args.length === 0) {
    return [];
  }

  // The sort() function below will not run if the list has only a single element.
  // This is problematic if that single element contains nested line items that need sorting, such as an item group!
  // So, if the single element contains an item group, recursively run this function to sort those nested line items.
  if (
    args.length === 1 &&
    args[0] &&
    isObjectLineItemGroupContainer(args[0]) &&
    args[0].groupId
  ) {
    sortByArrivalDate(args[0].lineItems);
  }

  return [
    ...args.sort((a, b) => {
      // Recursively run this function to sort nested line items inside item groups
      if (isObjectLineItemGroupContainer(a) && a.groupId) {
        sortByArrivalDate(a.lineItems);
      }
      // Recursively run this function to sort nested line items inside item groups
      if (isObjectLineItemGroupContainer(b) && b.groupId) {
        sortByArrivalDate(b.lineItems);
      }

      const dateA = getPromisedArrivalDate(a);
      const dateB = getPromisedArrivalDate(b);

      // Place elements with undefined/missing dates at the end of the list
      // If enabled, also place elements with dates in the past at the end of the list
      if (
        dateA === undefined ||
        (filterOutPastArrivalDates && isDateInPast(dateA))
      ) {
        return 1;
      }
      if (
        dateB === undefined ||
        (filterOutPastArrivalDates && isDateInPast(dateB))
      ) {
        return -1;
      }
      return dateA.isBefore(dateB) ? -1 : 1;
    }),
  ];
};

/**
 * Returns the list of grouped and sorted line items for display on the UI
 * @param { Object[] } lineItems - the list of line item objects as returned on an order by vistacart
 * @param { boolean } filterOutPastArrivalDates - if true, places line items with arrival dates in the past at the end of the list
 */
export const generateLineItemListDisplay = (
  lineItems: OrderSummaryLineItem[],
  filterOutPastArrivalDates?: boolean
) => {
  const itemContainers = generateItemContainers(lineItems);
  const sortedItemContainers = sortByArrivalDate(
    itemContainers,
    filterOutPastArrivalDates
  );
  return sortedItemContainers;
};

// Expose private functions for unit testing
export const _private = {
  generateItemContainers,
};
